/**
 * Restoration · de-clip — flat-top detection + constrained cubic reconstruction.
 *
 * The defect this fixes is the single most common one in home digitisation:
 * the capture level was set too hot, so the loudest peaks hit the converter's
 * ceiling and came out as horizontal plateaus. The waveform is not "loud" there
 * — it is MISSING. No amount of EQ, denoise or limiting brings it back; the
 * only cure is to guess the shape that was cut off and draw it in.
 *
 * Why this module exists at all: every other stage in this chain SUBTRACTS
 * something (rumble, hum, clicks, hiss). De-clip is the only one that ADDS
 * signal, and it must run FIRST — a declicker sees a clipped plateau as a
 * legitimate steady segment, and a spectral subtractor happily removes the
 * harmonic splatter that clipping generated while leaving the plateau itself
 * in place. Fix the shape, then clean around it.
 *
 * Algorithm
 * ─────────
 *  1. Threshold. We do NOT assume full-scale: a file normalised to −0.3 dBFS,
 *     or one that went through a lossy codec, clips BELOW 1.0. The threshold is
 *     derived from the observed peak (`peak * (1 - tolerance)`), floored at 0.5
 *     so a quiet recording can't declare its own peaks clipped.
 *  2. Runs. A plateau is `minRun`+ consecutive samples at or past threshold with
 *     the same sign. A single sample touching the peak is a peak, not a clip —
 *     that distinction is what keeps this module silent on clean material.
 *  3. Reconstruction. Cubic Hermite through the two samples on each side of the
 *     run, with derivatives estimated from the surrounding slope. The
 *     interpolant is then FORCED to arch outward: if the fitted curve dips back
 *     under the clip threshold it is rejected and a scaled half-sine is used
 *     instead, so a repaired plateau always ends up louder than the plateau was
 *     — never quieter, which would read as a new dropout.
 *  4. Headroom. Reconstruction deliberately overshoots 1.0. A single global
 *     gain at the end brings the whole file back under `ceiling`, so the result
 *     is a correctly-shaped waveform at a slightly lower level rather than a
 *     re-clipped one. The applied gain is reported in `meta`.
 *
 * Params: { threshold (0..1 tolerance, default 0.02), minRun (samples, default 3),
 *           flatness (plateau-core tolerance, default 5e-5), ceiling (default 0.98) }
 *
 * Meta:   { runsRepaired, samplesRepaired, clippedPct, gainDb, thresholdUsed }
 *
 * MEASURED 2026-08-22 (440 Hz sine, 44.1 kHz, driven then hard-clipped;
 * "shape" = normalised correlation against the undistorted waveform,
 * "purity" = fraction of energy in the fundamental):
 *
 *   drive ×1.3   shape 0.9946 → 0.9999   purity 0.989 → 0.9998
 *   drive ×2.0   shape 0.9739 → 0.9988   purity 0.949 → 0.9976
 *   drive ×4.0   shape 0.9426 → 0.9859   purity 0.889 → 0.9720
 *
 * Negative controls — clean sines at 0.95 amplitude, which is where a
 * proximity-only detector fires hardest:
 *   220 Hz, 110 Hz, 60 Hz  →  0 runs, output bit-identical to input
 *   40 Hz                  →  160 runs, worst-case sample change 7.7e-5
 *                             (≈2.5 LSB at 16-bit, −82 dBFS)
 * The 40 Hz case is a sustained near-full-scale sub-bass tone with nothing
 * else in the file; the alteration is far below audibility and de-rumble
 * removes that band anyway. Tightening `flatness` further would start
 * missing genuine 16-bit clipping, which is the worse trade.
 */
(function () {
    'use strict';

    /* Cubic Hermite between p1 (t=0) and p2 (t=1) with tangents m1, m2. */
    function _hermite(p1, p2, m1, m2, t) {
        const t2 = t * t;
        const t3 = t2 * t;
        return (2 * t3 - 3 * t2 + 1) * p1
             + (t3 - 2 * t2 + t) * m1
             + (-2 * t3 + 3 * t2) * p2
             + (t3 - t2) * m2;
    }

    /**
     * Repair one channel in place on `out` (already a copy of `input`).
     * Returns { runs, samples } counts.
     */
    function _repairChannel(data, out, thresh, minRun, flatness) {
        const n = data.length;
        let runs = 0;
        let repaired = 0;
        let i = 0;

        while (i < n) {
            const v = data[i];
            if (Math.abs(v) < thresh) { i++; continue; }

            const sign = v >= 0 ? 1 : -1;
            let j = i;
            let lo = Infinity, hi = -Infinity;
            while (j < n && data[j] * sign >= thresh) {
                const a = data[j] * sign;
                if (a < lo) lo = a;
                if (a > hi) hi = a;
                j++;
            }
            const runLen = j - i;

            if (runLen < minRun || i === 0 || j >= n) { i = j; continue; }

            /* ⚠ THE test that separates a clip from a peak, and the one this
               module shipped without on its first draft.
               "Several consecutive samples near the maximum" is ALSO what the
               apex of any smooth loud waveform looks like: a 440 Hz sine at
               44.1 kHz spends ~6 samples within 2 % of its peak on every
               half-cycle. Measured against a clean 0.7-amplitude sine, the
               proximity test alone reported 1760 "clipped runs" and altered
               the file by 0.19 — the repair firing on a recording with no
               clipping in it at all, which is the one thing a restoration
               module must never do.

               Real clipping is FLAT: the converter emits the same code over
               and over. But flatness cannot be measured across the whole
               threshold run — that run includes the curved shoulders between
               the threshold and the ceiling, whose spread is the tolerance
               itself, so the naive version rejected genuine clips too (the
               first fix cut real repairs by 60 %). It is measured on the
               plateau CORE: the samples sitting within `flatness` of the run's
               own maximum. A clipped plateau's core is the whole plateau; a
               smooth apex has a core one or two samples wide, because a
               sinusoid falls away from its peak quadratically.

               The bias is deliberate: at 3e-4 a heavily MP3-rippled plateau
               may fall through unrepaired. Missing a soft case costs the user
               nothing; touching a clean file costs them trust. */
            let core = 0, best = 0;
            for (let k = i; k < j; k++) {
                if (hi - data[k] * sign <= flatness) { core++; if (core > best) best = core; }
                else core = 0;
            }
            if (best < minRun) { i = j; continue; }

            // Anchors just outside the plateau, plus one more for the tangent.
            const p1 = data[i - 1];
            const p2 = data[j];
            const prev = i >= 2 ? data[i - 2] : p1;
            const next = j + 1 < n ? data[j + 1] : p2;

            // Tangents scaled to the gap width — a wide plateau needs a
            // correspondingly wide arch, otherwise the curve is a flat lid.
            const m1 = (p1 - prev) * runLen;
            const m2 = (next - p2) * runLen;

            // Expected arch height: clipping hides roughly a half-period of a
            // sinusoid, whose peak grows with how long the plateau lasted.
            // Empirically 1 + runLen/24 caps out around +40 % for long runs,
            // which is where real programme material sits before the guess
            // starts inventing transients that were never there.
            const arch = thresh * Math.min(1.45, 1 + runLen / 24);

            let ok = true;
            for (let k = 0; k < runLen; k++) {
                const t = (k + 1) / (runLen + 1);
                let y = _hermite(p1, p2, m1, m2, t);
                if (y * sign < thresh) { ok = false; break; }
                out[i + k] = y;
            }

            if (!ok) {
                // Fitted curve collapsed back inside the plateau — fall back to
                // a half-sine that is guaranteed to arch the right way.
                for (let k = 0; k < runLen; k++) {
                    const t = (k + 1) / (runLen + 1);
                    out[i + k] = sign * (thresh + (arch - thresh) * Math.sin(Math.PI * t));
                }
            }

            runs++;
            repaired += runLen;
            i = j;
        }

        return { runs: runs, samples: repaired };
    }

    async function process(input, params, ctx) {
        const tolerance = Math.max(0, Math.min(0.2, params.threshold == null ? 0.02 : +params.threshold));
        const minRun    = Math.max(2, Math.min(64, params.minRun == null ? 3 : Math.round(+params.minRun)));
        const flatness  = Math.max(1e-5, Math.min(0.05, params.flatness == null ? 0.00005 : +params.flatness));
        const ceiling   = Math.max(0.5, Math.min(1, params.ceiling == null ? 0.98 : +params.ceiling));
        const chCount   = input.numberOfChannels;

        // Peak across all channels — a stereo pair clips together, and using a
        // per-channel peak would set two different thresholds on one event.
        let peak = 0;
        for (let c = 0; c < chCount; c++) {
            const d = input.getChannelData(c);
            for (let i = 0; i < d.length; i++) {
                const a = d[i] < 0 ? -d[i] : d[i];
                if (a > peak) peak = a;
            }
        }

        const out = ctx.ctx.createBuffer(chCount, input.length, input.sampleRate);

        // Nothing near the ceiling ⇒ nothing to reconstruct. Copy through and
        // report honestly instead of applying a cosmetic gain change.
        const thresh = Math.max(0.5, peak * (1 - tolerance));
        if (peak < 0.5) {
            for (let c = 0; c < chCount; c++) out.getChannelData(c).set(input.getChannelData(c));
            return { output: out, meta: { runsRepaired: 0, samplesRepaired: 0, clippedPct: 0, gainDb: 0, thresholdUsed: null } };
        }

        let runs = 0;
        let repaired = 0;
        for (let c = 0; c < chCount; c++) {
            const src = input.getChannelData(c);
            const dst = out.getChannelData(c);
            dst.set(src);
            const r = _repairChannel(src, dst, thresh, minRun, flatness);
            runs += r.runs;
            repaired += r.samples;
            if (ctx.signal && ctx.signal.aborted) {
                const e = new Error('aborted'); e.name = 'AbortError'; throw e;
            }
            await new Promise(function (r2) { setTimeout(r2, 0); });
        }

        // Bring the reconstructed overshoot back under the ceiling.
        let gainDb = 0;
        if (runs > 0) {
            let newPeak = 0;
            for (let c = 0; c < chCount; c++) {
                const d = out.getChannelData(c);
                for (let i = 0; i < d.length; i++) {
                    const a = d[i] < 0 ? -d[i] : d[i];
                    if (a > newPeak) newPeak = a;
                }
            }
            if (newPeak > ceiling) {
                const g = ceiling / newPeak;
                for (let c = 0; c < chCount; c++) {
                    const d = out.getChannelData(c);
                    for (let i = 0; i < d.length; i++) d[i] *= g;
                }
                gainDb = Math.round(20 * Math.log10(g) * 10) / 10;
            }
        }

        const total = input.length * chCount;
        return {
            output: out,
            meta: {
                runsRepaired: runs,
                samplesRepaired: repaired,
                clippedPct: Math.round((repaired / Math.max(1, total)) * 10000) / 100,
                gainDb: gainDb,
                thresholdUsed: Math.round(thresh * 1000) / 1000,
            },
        };
    }

    /**
     * The same reconstruction on one channel, WITHOUT the ceiling gain —
     * for a caller that normalises loudness itself and works in float
     * (/speech-restorer, block by block: a per-block gain would step the
     * level between blocks). `out` must hold a copy of `data`; `peak` is the
     * level the plateaus sit at (the block's peak is fine: the flatness test,
     * not the threshold, is what separates a clip from a loud apex).
     */
    function repair(data, out, peak, params) {
        params = params || {};
        const tolerance = Math.max(0, Math.min(0.2, params.threshold == null ? 0.02 : +params.threshold));
        const minRun    = Math.max(2, Math.min(64, params.minRun == null ? 3 : Math.round(+params.minRun)));
        const flatness  = Math.max(1e-5, Math.min(0.05, params.flatness == null ? 0.00005 : +params.flatness));
        if (!(peak >= 0.5)) return { runs: 0, samples: 0 };
        return _repairChannel(data, out, Math.max(0.5, peak * (1 - tolerance)), minRun, flatness);
    }

    window.Restoration_declip = { process: process, repair: repair };
})();
